import { onMounted, onUnmounted, ref, Ref } from "vue";
import { getSocket } from "@/utils/socket";
import type { Socket } from "socket.io-client";
import type { MessageReceivedPayload } from "@/dtos/MessageWebhook.dto";

/**
 * Options for the useMessageWebhooks composable
 */
export interface UseMessageWebhooksOptions {
  /**
   * Callback for handling LinkedIn messages
   * @param payload - The message payload
   * @param helpers - Helper functions for message handling
   */
  onLinkedInMessage?: (
    payload: MessageReceivedPayload["payload"],
    helpers: {
      createMessage: (payload: MessageReceivedPayload["payload"]) => any;
      checkMessageExists: (newMessage: any, messages: any[]) => boolean;
    }
  ) => void;

  /**
   * Callback for handling WhatsApp messages
   * @param payload - The message payload
   * @param helpers - Helper functions for message handling
   */
  onWhatsAppMessage?: (
    payload: MessageReceivedPayload["payload"],
    helpers: {
      createMessage: (payload: MessageReceivedPayload["payload"]) => any;
      checkMessageExists: (newMessage: any, messages: any[]) => boolean;
    }
  ) => void;

  /**
   * Maximum number of processed event IDs to keep in memory (default: 1000)
   */
  maxEventIds?: number;

  /**
   * Callback for handling reaction add/remove events on a message
   * @param payload - The reaction payload
   */
  onMessageReaction?: (payload: {
    messageId: string;
    unipileMessageId: string;
    chatId: string;
    reactions: Array<{ value: string; sender_id: string; is_sender: boolean }>;
  }) => void;
}

/**
 * Creates a standardized message object from payload
 */
export function createMessageFromPayload(payload: MessageReceivedPayload["payload"]): any {
  return {
    id: payload.messageId || payload.unipileMessageId,
    messageId: payload.messageId,
    unipileMessageId: payload.unipileMessageId,
    conversationId: payload.chatId,
    senderId: payload.senderId,
    senderName: payload.senderName || "Unknown",
    text: payload.text || "",
    content: payload.text || "",
    channel: payload.channel,
    timestamp: payload.eventTimestamp || new Date().toISOString(),
    isOwn: (payload as any).isSender || false,
    isSender: (payload as any).isSender || false,
    readReceipts: [],
    readCount: 0,
    attachments: payload.metadata?.attachments || [],
    isPending: false,
  };
}

/**
 * Checks if a message already exists in the messages array
 */
export function checkMessageExists(newMessage: any, messages: any[]): boolean {
  return messages.some(
    (m) =>
      (m.id && m.id === newMessage.id) ||
      (m.messageId && m.messageId === newMessage.messageId) ||
      (m.unipileMessageId && m.unipileMessageId === newMessage.unipileMessageId)
  );
}

/**
 * Checks if a message matches a conversation by chatId, contactId, or senderId
 */
export function matchesConversation(
  payload: MessageReceivedPayload["payload"],
  conversation: {
    id: string;
    contactId?: string | null;
    participants?: Array<{ providerUserId?: string; is_self?: boolean }>;
  }
): boolean {
  const chatIdMatch =
    payload.chatId === conversation.id || (payload as any).unipileChatId === conversation.id;

  // Match by contactId
  const contactIdMatch = conversation.contactId && payload.contactId === conversation.contactId;

  // Match by senderId
  let senderIdMatch = false;
  if (payload.channel === "linkedin" && conversation.participants) {
    const otherParticipants = conversation.participants.filter((p) => !p.is_self);
    senderIdMatch = otherParticipants.some((p) => p.providerUserId === payload.senderId);
  }

  return chatIdMatch || contactIdMatch || senderIdMatch;
}

/**
 * Composable for handling message webhook events via Socket.IO
 * Provides real-time message event handling with deduplication
 *
 * @param options - Configuration options for the composable
 * @returns Object containing connection state and control functions
 */
export function useMessageWebhooks(options: UseMessageWebhooksOptions = {}) {
  const { onLinkedInMessage, onWhatsAppMessage, onMessageReaction, maxEventIds = 1000 } = options;

  const socket: Ref<Socket | null> = ref(null);
  const isConnected: Ref<boolean> = ref(false);
  const processedMessageIds: Ref<Set<string>> = ref(new Set<string>());

  /**
   * Check if message has already been processed
   */
  const isMessageProcessed = (messageId: string | null): boolean => {
    if (!messageId) return false;
    return processedMessageIds.value.has(messageId);
  };

  /**
   * Mark message as processed
   */
  const markMessageProcessed = (messageId: string | null): void => {
    if (!messageId) return;
    processedMessageIds.value.add(messageId);

    // Cleanup old message IDs if we exceed the limit
    if (processedMessageIds.value.size > maxEventIds) {
      const firstId = processedMessageIds.value.values().next().value;
      if (firstId) {
        processedMessageIds.value.delete(firstId);
      }
    }
  };

  /**
   * Handle message received event
   */
  const handleMessageReceived = (data: MessageReceivedPayload) => {
    try {
      const { payload } = data;
      if (!payload) return;

      const messageId = payload.unipileMessageId || payload.messageId;
      if (!messageId) return;

      // Skip if already processed (deduplication)
      if (isMessageProcessed(messageId)) {
        console.debug("Skipping duplicate message received event:", messageId);
        return;
      }

      markMessageProcessed(messageId);

      // Route to appropriate handler based on channel
      if (payload.channel === "linkedin" && onLinkedInMessage) {
        onLinkedInMessage(payload, {
          createMessage: createMessageFromPayload,
          checkMessageExists,
        });
      } else if (payload.channel === "whatsapp" && onWhatsAppMessage) {
        onWhatsAppMessage(payload, {
          createMessage: createMessageFromPayload,
          checkMessageExists,
        });
      }
    } catch (error) {
      console.error("Error handling message received event:", error);
    }
  };

  /**
   * Handle message reaction added/removed event
   */
  const handleMessageReaction = (data: {
    payload: {
      messageId: string;
      unipileMessageId: string;
      chatId: string;
      reactions: Array<{ value: string; sender_id: string; is_sender: boolean }>;
    };
  }) => {
    try {
      const { payload } = data;
      if (!payload) return;
      onMessageReaction?.(payload);
    } catch (error) {
      console.error("Error handling message reaction event:", error);
    }
  };

  /**
   * Setup socket connection and event listeners
   */
  const setupSocket = () => {
    try {
      socket.value = getSocket();
      isConnected.value = socket.value.connected;

      // Connection handlers
      socket.value.on("connect", () => {
        isConnected.value = true;
      });

      socket.value.on("disconnect", () => {
        isConnected.value = false;
      });

      socket.value.on("connect_error", (error) => {
        isConnected.value = false;
        console.error("❌ Message webhooks: Socket.IO connection error:", error);
      });

      // Message event handler
      socket.value.on("unipile:message:received", handleMessageReceived);

      // Reaction event handler
      socket.value.on("unipile:message:reaction", handleMessageReaction);
    } catch (error) {
      console.error("Failed to setup message webhooks socket:", error);
      isConnected.value = false;
    }
  };

  /**
   * Cleanup socket connection and event listeners
   */
  const cleanupSocket = () => {
    if (socket.value) {
      try {
        // Remove event listeners
        socket.value.off("unipile:message:received");
        socket.value.off("unipile:message:reaction");
        socket.value.off("connect");
        socket.value.off("disconnect");
        socket.value.off("connect_error");
      } catch (error) {
        console.error("Error cleaning up message webhooks socket:", error);
      }
    }
  };
  onMounted(() => {
    setupSocket();
  });

  onUnmounted(() => {
    cleanupSocket();
  });

  return {
    socket,
    isConnected,
    processedMessageIds,
    setupSocket,
    cleanupSocket,
    matchesConversation,
    createMessageFromPayload,
    checkMessageExists,
  };
}
